import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";
import { useModelUpload } from "../ModelSelector/useModelUpload";
import {
  deletePlaygroundItem,
  getPlaygroundItemLabel,
  resolvePlaygroundItemId,
  showDeleteConfirmationToast,
} from "../../utils/playgroundDelete";

const DEFAULT_PAGE_LIMIT = 10;

const normalizeModelItem = (item) => {
  if (!item) return null;
  const activeFlag =
    item?.bIsActive !== undefined
      ? Boolean(item.bIsActive && item.bIsActive !== "0" && item.bIsActive !== 0)
      : item?.isActive !== undefined
        ? Boolean(item.isActive && item.isActive !== "0" && item.isActive !== 0)
        : item?.status !== undefined
          ? Boolean(item.status === true || item.status === 1 || item.status === "1" || item.status === "active")
          : true;

  const id = resolvePlaygroundItemId(item) || item.id || item.iModelId || item.modelId;
  const images = Array.isArray(item.images) && item.images.length > 0
    ? item.images.filter(Boolean)
    : [item.front || item.image || item.sImage, item.back].filter(Boolean);

  const tierVal = item.tier || item.sTier || "";
  const sourceVal = item.source || (tierVal.toLowerCase().includes("ai") ? "ai" : "upload");

  return {
    ...item,
    id,
    modelId: id,
    name: item.name || item.title || item.sTitle || "Fashion Model",
    front: item.front || images[0] || "",
    back: item.back || images[1] || "",
    image: item.image || images[0] || "",
    images: images.length > 0 ? images : [""],
    tier: tierVal,
    source: sourceVal,
    bIsActive: activeFlag,
    isActive: activeFlag,
  };
};

export const useModelListing = () => {
  const { role: userRole, token } = useSelector((state) => state.auth || {});

  // Model type: "model" (Studio Models) or "user" (AI / User Personas)
  const [modelType, setModelType] = useState("model");
  const [gender, setGender] = useState("women");
  const [source, setSource] = useState("all"); // "all" | "upload" | "ai"
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modelToEdit, setModelToEdit] = useState(null);

  // Zoom modal
  const [zoomModal, setZoomModal] = useState({ open: false, front: "", back: "" });

  const { uploadModel, updateModel, isLoading: isUploadLoading } = useModelUpload();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleGenderChange = (newGender) => {
    setGender(newGender);
    setPage(1);
  };

  const handleModelTypeChange = (newType) => {
    setModelType(newType);
    setPage(1);
  };

  const handleSourceChange = (newSource) => {
    setSource(newSource);
    setPage(1);
  };

  const fetchModels = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const queryObj = {
        gender,
        type: modelType,
        search: debouncedSearch,
        page,
        limit,
      };

      if (source && source !== "all") {
        queryObj.source = source;
      }

      const query = new URLSearchParams(queryObj);

      const response = await apiHandler({
        url: `${endpoint.API_BASE_URL}${endpoint.GET_MODELS}?${query.toString()}`,
        method: "GET",
        authToken: { token },
        noCache: true,
      });

      const payload = response?.data;
      const rows = Array.isArray(payload?.data?.rows)
        ? payload.data.rows
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.rows)
            ? payload.rows
            : Array.isArray(payload)
              ? payload
              : [];

      let normalized = rows.map(normalizeModelItem).filter(Boolean);

      // Client-side fallback filter if backend didn't filter by source
      if (source && source !== "all") {
        const sourceMatches = normalized.filter((item) => item.source === source);
        if (sourceMatches.length > 0 || normalized.some((item) => item.source && item.source !== source)) {
          normalized = sourceMatches;
        }
      }

      setModels(normalized);

      const metaTotal = Number(payload?.data?.total ?? payload?.total ?? normalized.length) || 0;
      const metaPages = Math.max(1, Number(payload?.data?.totalPages ?? payload?.totalPages ?? Math.ceil(metaTotal / limit)) || 1);

      setTotal(metaTotal);
      setTotalPages(metaPages);
    } catch (err) {
      console.error("Error fetching models:", err);
      setError(err?.message || "Failed to load models.");
      setModels([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [token, gender, modelType, source, debouncedSearch, page, limit]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  // Toggle active/inactive
  const handleToggleModel = async (model) => {
    const itemId = resolvePlaygroundItemId(model);
    if (!itemId) {
      toast.error("Model ID not found.");
      return;
    }

    const previousState = Boolean(model.bIsActive);
    const newState = !previousState;

    // Optimistic UI update
    setModels((prev) =>
      prev.map((item) =>
        resolvePlaygroundItemId(item) === itemId
          ? { ...item, bIsActive: newState, isActive: newState }
          : item
      )
    );

    try {
      const response = await apiHandler({
        url: `${endpoint.TOGGLE_MODEL}/${itemId}/toggle`,
        method: "PATCH",
        authToken: { token },
        noCache: true,
      });

      if (
        (response?.status && response.status >= 400) ||
        response?.data?.status === "error" ||
        response?.data?.success === false
      ) {
        throw new Error(response?.data?.message || "Failed to update status");
      }

      toast.success(`Model ${newState ? "activated" : "deactivated"} successfully.`);
    } catch (err) {
      console.error("Model toggle error:", err);
      toast.error(err?.message || "Failed to update model status.");
      // Rollback
      setModels((prev) =>
        prev.map((item) =>
          resolvePlaygroundItemId(item) === itemId
            ? { ...item, bIsActive: previousState, isActive: previousState }
            : item
        )
      );
    }
  };

  // Delete Model
  const handleDeleteModel = (model) => {
    const itemId = resolvePlaygroundItemId(model);
    if (!itemId) {
      toast.error("Model is missing an ID.");
      return;
    }

    showDeleteConfirmationToast({
      title: "Delete model?",
      description: `This will permanently remove ${getPlaygroundItemLabel(model)} from the catalog.`,
      confirmLabel: "Delete model",
      onConfirm: async () => {
        try {
          const result = await deletePlaygroundItem({
            resource: "model",
            itemId,
            token,
          });

          if (result?.status >= 200 && result?.status < 300) {
            toast.success("Model deleted successfully.");
            setModels((prev) => prev.filter((item) => resolvePlaygroundItemId(item) !== itemId));
            setTotal((prev) => Math.max(0, prev - 1));
            return;
          }
          toast.error(result?.data?.error_message || result?.data?.error || "Failed to delete model.");
        } catch (error) {
          toast.error(error?.message || "Failed to delete model.");
        }
      },
    });
  };

  // Upload / Edit Submit
  // useModelUploadModal calls: onSubmit(formDataToSend, isEdit, modelId)
  const handleUploadSubmit = async (payload, isEdit, modelId) => {
    try {
      if (isEdit && modelId) {
        const result = await updateModel(modelId, payload);
        if (result.success) {
          toast.success("Model updated successfully!");
          handleCloseModal();
          fetchModels();
        } else {
          toast.error(`Update failed: ${result.error}`);
        }
      } else {
        const result = await uploadModel(payload);
        if (result.success) {
          toast.success("Model uploaded successfully!");
          handleCloseModal();
          fetchModels();
        } else {
          toast.error(`Upload failed: ${result.error}`);
        }
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  const handleOpenUploadModal = () => {
    setModelToEdit(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (model) => {
    setModelToEdit(model);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModelToEdit(null);
  };

  const openZoomModal = (front, back = "") => {
    setZoomModal({ open: true, front, back });
  };

  const closeZoomModal = () => {
    setZoomModal({ open: false, front: "", back: "" });
  };

  return {
    userRole,
    modelType,
    onModelTypeChange: handleModelTypeChange,
    gender,
    onGenderChange: handleGenderChange,
    source,
    onSourceChange: handleSourceChange,
    searchTerm,
    onSearchChange: (e) => setSearchTerm(e?.target ? e.target.value : e),
    models,
    loading,
    error,
    refreshModels: fetchModels,
    pagination: {
      page,
      limit,
      total,
      totalPages,
    },
    onPageChange: (newPage) => setPage(newPage),
    onLimitChange: (newLimit) => {
      setLimit(newLimit);
      setPage(1);
    },
    handleToggleModel,
    handleDeleteModel,
    addModelLocally: (newModel) => {
      setModels((prev) => [normalizeModelItem(newModel), ...prev]);
      setTotal((prev) => prev + 1);
    },
    // Modals
    isModalOpen,
    modelToEdit,
    isUploadLoading,
    handleOpenUploadModal,
    handleOpenEditModal,
    handleCloseModal,
    handleUploadSubmit,
    // Zoom
    zoomModal,
    openZoomModal,
    closeZoomModal,
  };
};
